/**
 * WebGL per-frame sync for icon ships, pickups, ship decorations,
 * speed zones, and icon/power-impact mid-effects.
 */

import { EntityType } from '../../entities.js';
import { getTintedImage } from '../../image_cache.js';
import { drawPickup, pickupAlpha, iconColorForEntity, iconDefForEntity } from '../canvas2d/primitives.js';
import { Sprite } from './pixi.js';
import { texFromCanvas, releaseCanvasTexture } from './pixi.js';
import { PICKUP_TEX_PADDING } from './units_const.js';
import {
    SPEED_ZONE_PULSE_PERIOD, SPEED_ZONE_RGB, ZONE_GRADIENT, pulse01,
    GRADIENT_CIRCLE_DEFAULTS, SOLID_SQUARE_DEFAULTS, PROHIBITION_DEFAULTS,
} from '../constants.js';

// -----------------------------------------------------------------------
// Icon units (frigates, corvettes, carriers, missiles)
// -----------------------------------------------------------------------

/**
 * @param {object} wr
 */
export function syncIconUnits(wr) {
    const units = wr._inner.entitySystem.units;
    const layer = wr._iconLayer;
    const pool  = wr._iconPool;

    pool.forEach(s => { s._seen = false; });

    for (let i = 0; i < units.length; i++) {
        const e = units[i];
        if (e.type === EntityType.FIGHTER) continue;
        if (e.type === EntityType.MINE || e.type === EntityType.PICKUP || e.type === EntityType.SLOW_ZONE) continue;

        const iconDef = iconDefForEntity(e);
        if (!iconDef) continue;
        const color     = iconColorForEntity(e);
        const texCanvas = getTintedImage(iconDef.path, color);
        if (!texCanvas) continue;

        let sprite = pool.get(e);
        if (!sprite) {
            sprite = new Sprite();
            sprite.anchor.set(0.5);
            layer.addChild(sprite);
            pool.set(e, sprite);
        }
        sprite._seen = true;

        const tex = texFromCanvas(texCanvas);
        if (sprite.texture !== tex) sprite.texture = tex;

        const baseSize = e.size * 2 * iconDef.scale;
        sprite.width  = baseSize;
        sprite.height = baseSize;
        sprite.x      = e.x;
        sprite.y      = e.y;
        sprite.alpha  = e.visual?.alpha ?? 1.0;

        if (iconDef.rotate) {
            const speed = Math.sqrt(e.velX * e.velX + e.velY * e.velY);
            if (e.visual && speed > 0.5) e.visual.facingAngle = Math.atan2(e.velY, e.velX);
            const facing = e.visual?.facingAngle ?? Math.atan2(e.velY, e.velX);
            sprite.rotation = facing + iconDef.rotation;
        }
    }

    pool.forEach((s, entity) => {
        if (!s._seen) {
            layer.removeChild(s);
            s.destroy({ texture: false });
            pool.delete(entity);
        }
    });
}

// -----------------------------------------------------------------------
// Pickups (stars + text triggers)
// -----------------------------------------------------------------------

/**
 * @param {object} wr
 */
export function syncPickups(wr) {
    const units = wr._inner.entitySystem.units;
    const layer = wr._pickupLayer;
    const pool  = wr._pickupPool;

    pool.forEach(s => { s._seen = false; });

    for (let i = 0; i < units.length; i++) {
        const e = units[i];
        if (e.type !== EntityType.PICKUP) continue;
        if (e.hiddenInGame) continue;

        const shape = e.subtype === 'text_trigger' ? 'text_trigger' : 'star';
        const size  = e.size || (shape === 'text_trigger' ? 10 : 8);
        const tex   = pickupTextureFor(wr, shape, size);
        if (!tex) continue;

        let sprite = pool.get(e);
        if (!sprite) {
            sprite = new Sprite();
            sprite.anchor.set(0.5);
            layer.addChild(sprite);
            pool.set(e, sprite);
        }
        sprite._seen = true;

        if (sprite.texture !== tex) sprite.texture = tex;

        const drawSize = (size + PICKUP_TEX_PADDING) * 2;
        sprite.width  = drawSize;
        sprite.height = drawSize;
        sprite.x      = e.x;
        sprite.y      = e.y;
        sprite.alpha  = (e.visual?.alpha ?? 1) * pickupAlpha(e);
    }

    pool.forEach((s, entity) => {
        if (!s._seen) {
            layer.removeChild(s);
            s.destroy({ texture: false });
            pool.delete(entity);
        }
    });
}

/**
 * Lazily rasterize a pickup shape via the shared Canvas2D drawPickup function.
 * @param {object} wr
 * @param {'star'|'text_trigger'} shape
 * @param {number} size
 * @returns {import('pixi.js').Texture | null}
 */
export function pickupTextureFor(wr, shape, size) {
    const key = `${shape}:${size}`;
    let tex = wr._pickupTexCache.get(key);
    if (tex) return tex;

    const dim = (size + PICKUP_TEX_PADDING) * 2;
    const canvas = document.createElement('canvas');
    canvas.width  = dim;
    canvas.height = dim;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const stub = {
        x: dim / 2, y: dim / 2, size,
        subtype: shape === 'text_trigger' ? 'text_trigger' : 'tech_boost',
        visual: { alpha: 1 }
    };
    drawPickup(stub, ctx);

    tex = texFromCanvas(canvas);
    wr._pickupTexCache.set(key, tex);
    return tex;
}

// -----------------------------------------------------------------------
// Ship decorations (underlays + overlays)
// -----------------------------------------------------------------------

/**
 * Sync one decoration layer for all non-fighter units.
 * @param {object} wr
 * @param {Array} units
 * @param {Map} spritePool
 * @param {import('pixi.js').Container} spriteLayer
 * @param {import('pixi.js').Graphics} graphicsObj
 * @param {'underlays'|'overlays'} decorKey
 * @param {number} dt
 * @param {number} time
 * @param {number} [sizeScale=1]
 */
export function syncDecorLayer(wr, units, spritePool, spriteLayer, graphicsObj, decorKey, dt, time, sizeScale = 1) {
    spritePool.forEach(sprites => { sprites._seen = false; });

    for (let i = 0; i < units.length; i++) {
        const entity = units[i];
        if (entity.type === EntityType.FIGHTER) continue;
        if (entity.type === EntityType.PICKUP || entity.type === EntityType.SLOW_ZONE) continue;

        const decors = entity.visual?.[decorKey];
        if (!decors || decors.length === 0) continue;

        const baseColor = iconColorForEntity(entity);

        let sprites = spritePool.get(entity);
        if (!sprites || sprites.length !== decors.length) {
            if (sprites) {
                for (let k = 0; k < sprites.length; k++) {
                    if (sprites[k]) { spriteLayer.removeChild(sprites[k]); sprites[k].destroy({ texture: false }); }
                }
            }
            sprites = new Array(decors.length).fill(null);
            spritePool.set(entity, sprites);
        }
        sprites._seen = true;

        for (let j = 0; j < decors.length; j++) {
            const deco = decors[j];
            const svgDeco = (deco.type === 'svg') ? deco
                          : (deco.type === 'prohibition' && deco.inner?.type === 'svg') ? deco.inner
                          : (deco.type === 'gradient-circle') ? deco
                          : null;

            if (svgDeco) {
                let canvas = null;
                if (svgDeco.type === 'svg') {
                    const opts  = svgDeco.opts;
                    const color = opts.color ?? baseColor;
                    canvas = getTintedImage(svgDeco.src, color);
                } else if (svgDeco.type === 'gradient-circle') {
                    canvas = getGradientCircleCanvas(deco, entity, baseColor);
                }

                if (!canvas) { if (sprites[j]) sprites[j].visible = false; }
                else {
                    let sprite = sprites[j];
                    if (!sprite) {
                        sprite = new Sprite();
                        sprite.anchor.set(0.5);
                        spriteLayer.addChild(sprite);
                        sprites[j] = sprite;
                    }

                    const tex = texFromCanvas(canvas);
                    if (sprite.texture !== tex) sprite.texture = tex;

                    if (svgDeco.type === 'svg') {
                        const opts = svgDeco.opts;
                        const baseSize = entity.size * 2;
                        const size = ((opts.size != null) ? opts.size : baseSize * (opts.sizeMult ?? 1.0)) * sizeScale;
                        sprite.width  = size;
                        sprite.height = size;
                        sprite.x      = entity.x;
                        sprite.y      = entity.y;
                        const entityAlpha = entity.visual?.alpha ?? 1.0;
                        sprite.alpha = entityAlpha * (opts.alpha ?? 1.0);
                        let angle = opts.rotation ?? 0;
                        if (opts.rotateWithShip && entity.visual?.facingAngle != null) {
                            angle += entity.visual.facingAngle;
                        }
                        if (opts.spinPerSec) {
                            deco._angle = (deco._angle ?? 0) + dt * opts.spinPerSec * Math.PI * 2;
                            angle += deco._angle;
                        }
                        sprite.rotation = angle;
                        sprite.visible  = opts.visible ? opts.visible(entity) : true;
                    } else {
                        const opts = svgDeco.opts;
                        const radius = ((opts.radius != null) ? opts.radius : entity.size * (opts.sizeMult ?? GRADIENT_CIRCLE_DEFAULTS.sizeMult)) * sizeScale;
                        sprite.width  = radius * 2;
                        sprite.height = radius * 2;
                        sprite.x      = entity.x;
                        sprite.y      = entity.y;
                        const entityAlpha = entity.visual?.alpha ?? 1.0;
                        let alpha = opts.alpha ?? GRADIENT_CIRCLE_DEFAULTS.alpha;
                        if (opts.pulsePerSec) {
                            alpha *= pulse01(time * opts.pulsePerSec);
                        }
                        sprite.alpha   = entityAlpha * alpha;
                        sprite.visible = opts.visible ? opts.visible(entity) : true;
                    }
                }
            }

            if (deco.type !== 'svg' && deco.type !== 'gradient-circle') {
                drawDecoPixiGraphics(graphicsObj, deco, entity, baseColor, time, sizeScale);
            }
        }
    }

    spritePool.forEach((sprites, entity) => {
        if (!sprites._seen) {
            for (let k = 0; k < sprites.length; k++) {
                if (sprites[k]) { spriteLayer.removeChild(sprites[k]); sprites[k].destroy({ texture: false }); }
            }
            // Entity is gone — its per-deco gradient canvases are unique to it,
            // so their textures must be destroyed (unlike the shared tinted-SVG
            // textures kept alive above).
            const decors = entity.visual?.[decorKey];
            if (decors) {
                for (let k = 0; k < decors.length; k++) {
                    if (decors[k]._gradCanvas) {
                        releaseCanvasTexture(decors[k]._gradCanvas);
                        delete decors[k]._gradCanvas;
                        delete decors[k]._gradCacheKey;
                    }
                }
            }
            spritePool.delete(entity);
        }
    });
}

/**
 * Return a pre-rendered HTMLCanvasElement for a gradient-circle decoration,
 * cached on deco._gradCanvas. Rebuilt only when color changes.
 * @param {object} deco
 * @param {object} entity
 * @param {string} _baseColor  (unused — colorRGB resolved inside)
 * @returns {HTMLCanvasElement|null}
 */
export function getGradientCircleCanvas(deco, entity, _baseColor) {
    const opts = deco.opts;
    const colorRGB = typeof opts.colorRGB === 'function'
        ? opts.colorRGB(entity)
        : (opts.colorRGB ?? GRADIENT_CIRCLE_DEFAULTS.colorRGB);
    const [r, g, b] = colorRGB;
    const cacheKey = `${Math.round(r)},${Math.round(g)},${Math.round(b)}`;

    if (deco._gradCanvas && deco._gradCacheKey === cacheKey) return deco._gradCanvas;
    releaseCanvasTexture(deco._gradCanvas); // color changed — drop the old texture

    const SIZE = 128;
    const c = document.createElement('canvas');
    c.width  = SIZE;
    c.height = SIZE;
    const cx  = c.getContext('2d');
    const cx2 = SIZE / 2;
    const grad = cx.createRadialGradient(cx2, cx2, 0, cx2, cx2, cx2);
    grad.addColorStop(0, `rgba(0,0,0,1)`);
    grad.addColorStop(1, `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},1)`);
    cx.fillStyle = grad;
    cx.beginPath();
    cx.arc(cx2, cx2, cx2, 0, Math.PI * 2);
    cx.fill();

    deco._gradCanvas   = c;
    deco._gradCacheKey = cacheKey;
    return c;
}

/**
 * Draw a non-SVG decoration into a Pixi Graphics object.
 * @param {import('pixi.js').Graphics} g
 * @param {object} deco
 * @param {object} entity
 * @param {string} baseColor
 * @param {number} time
 * @param {number} [sizeScale=1]
 */
export function drawDecoPixiGraphics(g, deco, entity, baseColor, time, sizeScale = 1) {
    if (deco.opts?.visible && !deco.opts.visible(entity)) return;
    const entityAlpha = entity.visual?.alpha ?? 1.0;

    if (deco.type === 'gradient-circle') {
        const opts = deco.opts;
        const radius = ((opts.radius != null) ? opts.radius : entity.size * (opts.sizeMult ?? GRADIENT_CIRCLE_DEFAULTS.sizeMult)) * sizeScale;
        const colorRGB = typeof opts.colorRGB === 'function' ? opts.colorRGB(entity) : (opts.colorRGB ?? GRADIENT_CIRCLE_DEFAULTS.colorRGB);
        const [r, gr, b] = colorRGB;
        let alpha = opts.alpha ?? GRADIENT_CIRCLE_DEFAULTS.alpha;
        if (opts.pulsePerSec) {
            alpha *= pulse01(time * opts.pulsePerSec);
        }
        alpha *= entityAlpha;
        const hexColor = (Math.round(r) << 16) | (Math.round(gr) << 8) | Math.round(b);
        g.circle(entity.x, entity.y, radius).fill({ color: hexColor, alpha });

    } else if (deco.type === 'solid-square') {
        const opts = deco.opts;
        const s = (opts.size ?? SOLID_SQUARE_DEFAULTS.size) * sizeScale;
        const color = opts.color ?? baseColor;
        const alpha = entityAlpha * (opts.alpha ?? SOLID_SQUARE_DEFAULTS.alpha);
        g.rect(entity.x - s / 2, entity.y - s / 2, s, s).fill({ color, alpha });
        // stroke: null disables the outline (same semantics as solidSquareDecoration).
        const strokeColor = opts.stroke === undefined ? SOLID_SQUARE_DEFAULTS.stroke : opts.stroke;
        if (strokeColor) {
            g.rect(entity.x - s / 2, entity.y - s / 2, s, s).stroke({ color: strokeColor, width: opts.strokeWidth ?? SOLID_SQUARE_DEFAULTS.strokeWidth });
        }

    } else if (deco.type === 'prohibition') {
        if (deco.inner?.type !== 'svg') {
            drawDecoPixiGraphics(g, deco.inner, entity, baseColor, time, sizeScale);
        }
        const opts    = deco.opts;
        const baseSize = entity.size * 2;
        const size    = ((opts.size != null) ? opts.size : baseSize * (opts.sizeMult ?? 1.0)) * sizeScale;
        const alpha   = entityAlpha * (opts.prohibitionAlpha ?? PROHIBITION_DEFAULTS.alpha);
        const color   = opts.prohibitionColor ?? PROHIBITION_DEFAULTS.color;
        const r       = size / 2 * 0.85;
        const lw      = Math.max(1.5, size * 0.1);
        const d       = r * 0.707;

        g.circle(entity.x, entity.y, r).stroke({ color, alpha, width: lw });
        g.moveTo(entity.x - d, entity.y - d)
         .lineTo(entity.x + d, entity.y + d)
         .stroke({ color, alpha, width: lw });
    }
}

// -----------------------------------------------------------------------
// Speed zones
// -----------------------------------------------------------------------

/**
 * @param {object} wr
 * @param {number} time
 */
export function syncSpeedZones(wr, time) {
    const container = wr._speedZoneContainer;
    const pool      = wr._speedZonePool;
    const es        = wr._inner.entitySystem;

    pool.forEach(s => { s._seen = false; });

    if (es.speedZones) {
        const pulseOpacity = pulse01((time % SPEED_ZONE_PULSE_PERIOD) / SPEED_ZONE_PULSE_PERIOD);

        for (let i = 0; i < es.speedZones.length; i++) {
            const zone = es.speedZones[i];
            const szd  = zone.zoneData;
            if (!szd) continue;
            const zr = zone.getSlowZoneRadiusAt ? zone.getSlowZoneRadiusAt(time) : szd.radius;
            if (zr <= 0) continue;

            let sprite = pool.get(zone);
            if (!sprite) {
                sprite = new Sprite();
                sprite.anchor.set(0.5);
                container.addChild(sprite);
                pool.set(zone, sprite);
            }
            sprite._seen = true;

            const [rr, rg, rb] = szd.colorRGB || SPEED_ZONE_RGB;
            const cacheKey = `${szd.multiplier > 1 ? 'fast' : 'slow'},${Math.round(rr)},${Math.round(rg)},${Math.round(rb)}`;
            if (!zone._gradCanvas || zone._gradCacheKey !== cacheKey) {
                releaseCanvasTexture(zone._gradCanvas); // color changed — drop the old texture
                const SZ  = 128;
                const c   = document.createElement('canvas');
                c.width = c.height = SZ;
                const cx  = c.getContext('2d');
                const mid = SZ / 2;
                const grad = cx.createRadialGradient(mid, mid, 0, mid, mid, mid);
                // Stops are baked at full strength; the pulse is applied per
                // frame via sprite.alpha (equivalent to Canvas2D multiplying
                // pulseOpacity into each stop).
                if (szd.multiplier > 1) {
                    for (const [offset, alpha] of ZONE_GRADIENT.fast) {
                        grad.addColorStop(offset, `rgba(${Math.round(rr)},${Math.round(rg)},${Math.round(rb)},${alpha})`);
                    }
                } else {
                    grad.addColorStop(0, `rgba(0,0,0,${ZONE_GRADIENT.slowCenterAlpha})`);
                    grad.addColorStop(1, `rgba(${Math.round(rr)},${Math.round(rg)},${Math.round(rb)},${ZONE_GRADIENT.slowEdgeAlpha})`);
                }
                cx.fillStyle = grad;
                cx.beginPath();
                cx.arc(mid, mid, mid, 0, Math.PI * 2);
                cx.fill();
                zone._gradCanvas   = c;
                zone._gradCacheKey = cacheKey;
            }
            const tex = texFromCanvas(zone._gradCanvas);
            if (sprite.texture !== tex) sprite.texture = tex;

            sprite.x      = zone.x;
            sprite.y      = zone.y;
            sprite.width  = zr * 2;
            sprite.height = zr * 2;
            sprite.alpha  = pulseOpacity;
        }
    }

    pool.forEach((sprite, zone) => {
        if (!sprite._seen) {
            container.removeChild(sprite);
            sprite.destroy({ texture: false });
            // Zone is gone — its gradient canvas is unique to it.
            releaseCanvasTexture(zone._gradCanvas);
            delete zone._gradCanvas;
            delete zone._gradCacheKey;
            pool.delete(zone);
        }
    });
}

// -----------------------------------------------------------------------
// IconEffect + PowerImpactCircle mid-effects
// -----------------------------------------------------------------------

/**
 * Advance icon effects and power impacts per frame.
 * @param {object} wr
 * @param {number} dt
 */
export function syncPixiMidTopEffects(wr, dt) {
    wr._midEffectGraphics.clear();

    const icons = wr._iconEffects;
    let i = 0;
    while (i < icons.length) {
        const eff = icons[i];
        if (eff.advance(dt)) {
            eff.cleanup();
            icons[i] = icons[icons.length - 1]; icons.pop();
            continue;
        }
        const loaded = eff.syncSprite(
            wr._groundIconEffectContainer,
            wr._topIconEffectContainer,
            getTintedImage, texFromCanvas, Sprite
        );
        if (!loaded) { i++; continue; }
        i++;
    }

    const impacts = wr._powerImpacts;
    i = 0;
    while (i < impacts.length) {
        const eff = impacts[i];
        if (eff.advance(dt)) { impacts[i] = impacts[impacts.length - 1]; impacts.pop(); continue; }
        eff.drawCircle(wr._midEffectGraphics);
        i++;
    }
}
